import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import React, { useEffect,useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import APIGateway from '@/api';
import { Button } from '@/components/button/button';
import OptionRow from '@/components/optionRow/optionRow';
import { EXAMPLE_TYPES, ExampleTypeEnum, SourceConfigSnowflake } from '@/types/evals';

import styles from './modalSourceCreate.module.scss';
import { snowflakeConfigSchema } from './schemas';

const formSchema = z.object( {
  example_type: z.enum( [ EXAMPLE_TYPES.CONVERSATION, EXAMPLE_TYPES.MESSAGE ] ),
  config: snowflakeConfigSchema
} );

type FormData = z.infer<typeof formSchema>;

type SnowflakeConfigFormProps = {
  initialData?: {
    example_type: ExampleTypeEnum;
    config: SourceConfigSnowflake;
  };
  // snake case keys are fields directly to/from python servers
  source_name: string;
  source_description?: string;
  source_type: 'snowflake';
  onSubmit: ( data: FormData ) => void;
  onCancel: () => void;
  onBack: () => void;
  isPending?: boolean;
  isEditMode: boolean;
};

export const SnowflakeConfigForm: React.FC<SnowflakeConfigFormProps> = ( {
  initialData,
  source_name,
  source_description,
  source_type,
  onSubmit: onSubmitProp,
  onCancel,
  onBack,
  isPending,
  isEditMode
} ) => {
  const [ validationSuccess, setValidationSuccess ] = useState( false );
  const [ validationError, setValidationError ] = useState<string>();
  const [ isValidating, setIsValidating ] = useState( false );
  const isMountedRef = useRef( true );

  useEffect( () => {
    return () => {
      isMountedRef.current = false;
    };
  }, [] );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    getValues,
    formState: { errors }
  } = useForm<FormData>( {
    resolver: zodResolver( formSchema ),
    defaultValues: initialData || {
      config: {}
    }
  } );

  const exampleType = watch( 'example_type' );

  const onSubmit = ( data: FormData ) => {
    onSubmitProp( data );
  };

  const handleValidateClick = async() => {
    // Trigger form validation first
    const isFormValid = await trigger();
    if ( !isFormValid ) return;

    if ( !isMountedRef.current ) return;
    setIsValidating( true );
    setValidationError( undefined );
    setValidationSuccess( false );

    try {
      const data = getValues();
      const validationData = {
        name: source_name,
        source_description,
        source_type,
        config: data.config,
        example_type: data.example_type
      };

      const result = await APIGateway.evals.sourceValidate( validationData );

      if ( !isMountedRef.current ) return;
      if ( result.status === 'success' ) {
        console.log( 'Validation successful!' );
        setIsValidating( false );
        setValidationSuccess( true );
      } else {
        setIsValidating( false );
        setValidationError( result.message );
      }
    } catch( error ) {
      if ( !isMountedRef.current ) return;
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Validation failed. Please check your configuration and try again.';
      setIsValidating( false );
      setValidationError( errorMessage );
    }
  };

  return (
    <form id="source-config-form" onSubmit={ handleSubmit( onSubmit ) } className={ styles.form_wrapper }>
      <section className={ styles.layout__source_type }>
        <h2>Create Source - Snowflake</h2>

        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label
            className={ clsx(
              'auth_flow_label',
              errors.config?.database_name && 'required'
            ) }
            htmlFor="config.databaseName"
          >
            Database Name <span className={ styles.required_asterisk }>*</span>
          </label>
          <input
            className={ clsx( 'auth_flow_input', errors.config?.database_name && styles.input_error ) }
            id="config.databaseName"
            type="text"
            data-testid="source-config-snowflake-database-name"
            disabled={ isPending }
            { ...register( 'config.database_name' ) }
          />
          {errors.config?.database_name && (
            <div className={ styles.error_message }>
              {errors.config.database_name.message || 'Database name is required'}
            </div>
          )}
        </div>

        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label
            className={ clsx(
              'auth_flow_label',
              errors.config?.schema_name && 'required'
            ) }
            htmlFor="config.schemaName"
          >
            Schema Name
          </label>
          <input
            className={ clsx( 'auth_flow_input', errors.config?.schema_name && styles.input_error ) }
            id="config.schemaName"
            type="text"
            data-testid="source-config-snowflake-schema-name"
            disabled={ isPending }
            { ...register( 'config.schema_name' ) }
          />
          {errors.config?.schema_name && (
            <div className={ styles.error_message }>
              {errors.config.schema_name.message}
            </div>
          )}
        </div>

        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label
            className={ clsx(
              'auth_flow_label',
              errors.config?.credential_ref && 'required'
            ) }
            htmlFor="config.credentialRef"
          >
            Credential Reference <span className={ styles.required_asterisk }>*</span>
          </label>
          <input
            className={ clsx( 'auth_flow_input', errors.config?.credential_ref && styles.input_error ) }
            id="config.credentialRef"
            type="text"
            data-testid="source-config-snowflake-credential-reference"
            disabled={ isPending }
            { ...register( 'config.credential_ref' ) }
          />
          {errors.config?.credential_ref && (
            <div className={ styles.error_message }>
              {errors.config.credential_ref.message || 'Credential reference is required'}
            </div>
          )}
        </div>

        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label
            className={ clsx(
              'auth_flow_label',
              errors.config?.query && 'required'
            ) }
            htmlFor="config.query"
          >
            Query <span className={ styles.required_asterisk }>*</span>
          </label>
          <textarea
            className={ clsx( errors.config?.query && styles.input_error ) }
            id="config.query"
            data-testid="source-config-snowflake-query"
            disabled={ isPending }
            { ...register( 'config.query' ) }
          />
          {errors.config?.query && (
            <div className={ styles.error_message }>
              {errors.config.query.message || 'Query is required'}
            </div>
          )}
        </div>

        <div className={ clsx( 'layout__col', 'pad' ) } data-testid="example-type-selection">
          <OptionRow
            value={ exampleType }
            onSelect={ ( value: ExampleTypeEnum ) => setValue( 'example_type', value ) }
            title={
              (
                <p className={ clsx( errors.example_type && 'required' ) }>
                  Select Example Type <span className={ styles.required_asterisk }>*</span>
                </p>
              )
            }
            options={ [
              {
                display: EXAMPLE_TYPES.CONVERSATION,
                value: EXAMPLE_TYPES.CONVERSATION
              },
              {
                display: EXAMPLE_TYPES.MESSAGE,
                value: EXAMPLE_TYPES.MESSAGE
              }
            ] }
            disabled={ isEditMode || isPending }
          />
          {errors.example_type && (
            <div className={ styles.error_message }>
              {errors.example_type.message || 'Please select an example type'}
            </div>
          )}
        </div>

        {validationError && (
          <div className={ clsx( 'layout__row', 'pad' ) }>
            <div className="layout__error" data-testid="validation-error-message">
              {validationError}
            </div>
          </div>
        )}
      </section>

      <div className={ styles.form_actions }>
        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <Button
            variant="secondary"
            className={ styles.validate_button }
            disabled={ isValidating || isPending }
            loading={ isValidating }
            onClick={ handleValidateClick }
            data-testid="source-validate-button"
          >
            Validate
          </Button>
          {validationSuccess && !isValidating && (
            <span className={ styles.validation_success } data-testid="validation-success-indicator">
              ✓ Valid
            </span>
          )}
        </div>
      </div>

      <div className={ styles.form_actions }>
        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <Button
            variant="secondary"
            size="sm"
            onClick={ onCancel }
            disabled={ isPending }
            data-testid="source-cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={ onBack }
            disabled={ isPending }
            data-testid="source-back-button"
          >
            Back
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            loading={ isPending }
            disabled={ isPending }
            data-testid="source-create-button"
          >
            Create
          </Button>
        </div>
      </div>
    </form>
  );
};
